import { nowIsoUtc } from "@nijiurachan/js/util/time";
import { useLayoutEffect, useRef, useState } from "react";
import { findMixLineupEntry } from "@/config/mixLineup";
import {
  completeDockingOnboarding,
  shouldShowDockingOnboarding,
} from "@/features/mix-onboarding/lib/dockingOnboarding";
import type { PwaInstallPromptStore } from "@/features/mix-onboarding/lib/pwaInstallPrompt";
import {
  createMediator,
  type EventNode,
  type TypedEvent,
} from "@/shared/lib/componentHierarchy";
import { parseMixRouteThreadSlug } from "@/shared/lib/threadRoutePath";
import {
  useComponentNode,
  useHierarchyLifetime,
  useMediatorSnapshot,
} from "@/shared/ui/component-hierarchy/hooks";

type GateEvent =
  | { type: "docking-gate.complete"; isAdult: boolean }
  | { type: "docking-gate.sync"; isDesktop: boolean };
type GateEffect =
  | { type: "complete"; isAdult: boolean }
  | { type: "stop-capture" };

export function useDockingGatePresenter(
  isMixCatalog: boolean,
  isDesktop: boolean,
  pathname: string,
  installPromptStore: PwaInstallPromptStore,
): {
  node: EventNode<TypedEvent>;
  isOpen: boolean;
  requiresAdultConfirmation: boolean;
  onComplete: (isAdult: boolean) => void;
} {
  const directThreadSlug = parseMixRouteThreadSlug(pathname);
  const requiresAdultConfirmation =
    directThreadSlug !== undefined &&
    findMixLineupEntry(directThreadSlug)?.isAdult === true;
  const latestStore = useRef(installPromptStore);
  const [mediator] = useState(() =>
    createMediator<boolean, GateEvent, GateEffect>(
      isMixCatalog &&
        shouldShowDockingOnboarding(undefined, requiresAdultConfirmation),
      (state, event) =>
        event.type === "docking-gate.complete"
          ? {
              status: "handled",
              state: false,
              effects: state
                ? [{ type: "complete", isAdult: event.isAdult }]
                : [],
            }
          : {
              status: "handled",
              state,
              effects:
                !state || event.isDesktop ? [{ type: "stop-capture" }] : [],
            },
      {
        runEffect(effect: GateEffect): void {
          latestStore.current.stopCapture();
          if (effect.type === "complete")
            completeDockingOnboarding(nowIsoUtc(), effect.isAdult);
        },
      },
    ),
  );
  useHierarchyLifetime(mediator);
  const isOpen = useMediatorSnapshot(mediator);
  const node = useComponentNode((event) => {
    if (
      event.type !== "docking-gate.complete" &&
      event.type !== "docking-gate.sync"
    )
      return "unhandled";
    return mediator.dispatch(event as GateEvent).status === "handled"
      ? "handled"
      : "unhandled";
  });
  useLayoutEffect(() => {
    latestStore.current = installPromptStore;
    const event: GateEvent = { type: "docking-gate.sync", isDesktop };
    node.dispatch(event);
  }, [node, isDesktop, installPromptStore]);
  return {
    node,
    isOpen,
    requiresAdultConfirmation,
    onComplete(isAdult: boolean): void {
      const event: GateEvent = { type: "docking-gate.complete", isAdult };
      node.dispatch(event);
    },
  };
}
